/**
 * AppHeader — the standard TitleFlow Operations header (56px, 3-zone).
 * BM-007: MOVED verbatim out of App.js `Dashboard` so standalone workspaces
 * (Break Management) reuse the same header instead of cloning it.
 *
 * Props
 *  - activeView   view key to highlight ('orders' | 'my-workspace' | 'team-management' | …) or null for none
 *  - onSelectView (view) => void; defaults to navigating via VIEW_TO_ROUTE (same as Dashboard.setActiveView)
 */
import { useInShell } from './ModuleShell';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, FileText, LogOut, ClipboardList, Home, MonitorCog, User, ChevronDown,
  BarChart3, Zap, DollarSign, Package, Coffee, PenLine,
} from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import TitleFlowLogo from '../TitleFlowLogo';
import NotificationBell from './NotificationBell';
import WorkspaceSwitcher from '../WorkspaceSwitcher';
import TeamActivityPanel from '../dashboard/TeamActivityPanel';
import { SessionHealthIndicator, BreakButton, SystemDowntimeButton } from '../session';
import { useAuth } from '../../App';
import { holdsTypingRole, holdsRole } from '../../utils/heldRoles';
import { useWorkspacePresentation } from '../../hooks/useWorkspacePresentation';
import { isJurisdictionRequestor } from '../../utils/jurisdictionRequestorGuard';
import {
  BLUE_HEADER_ROLES, VIEW_TO_ROUTE, getDefaultRoute, isITRole, isAccountingRole, getRoleDisplayName,
} from '../../lib/dashboardViews';

const AppHeaderInner = ({ activeView = null, onSelectView }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Jun 12, 2026 — Team dropdown is controlled so it deterministically
  // closes on item selection and on ANY route change.
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);
  useEffect(() => {
    setTeamMenuOpen(false);
  }, [location.pathname]);

  const isJR = isJurisdictionRequestor(user);
  // OD-PARITY-001: native order_desk OR Order Desk workspace + orders:view → Order Desk look
  const { isOrderDeskView, isProcessorWorkspaceView } = useWorkspacePresentation(user);
  // HEADER-BLUE-002: single source of truth for the blue Operations header.
  const headerIsBlue = BLUE_HEADER_ROLES.includes(user?.role) || isOrderDeskView || isProcessorWorkspaceView;

  const setActiveView = onSelectView || ((view) => navigate(VIEW_TO_ROUTE[view] || '/dashboard'));

  // LOGOUT-FLICKER-001: logout() and navigate() in the same synchronous tick.
  const handleLogout = () => {
    logout({ skipRedirect: true });
    navigate('/login', { replace: true });
  };

  return (
      <header className={`${headerIsBlue ? 'header-blue' : 'header-frosted'} sticky top-0 z-50`} data-testid={isOrderDeskView ? 'order-desk-header' : undefined}>
        <div className="mx-auto px-6" style={{ maxWidth: '1440px' }}>
          <div className="flex items-center justify-between h-14">
            {/* LEFT ZONE: Logo + Primary Navigation */}
            <div className="flex items-center gap-1 pl-1 min-w-0">
              <TitleFlowLogo
                variant={headerIsBlue ? 'darkBright' : 'compact'}
                className="cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                style={{ height: '32px', width: 'auto' }}
                onClick={() => {
                  if (isITRole(user?.role)) {
                    navigate(getDefaultRoute(user?.role));
                  } else if (isAccountingRole(user?.role)) {
                    navigate('/admin/billing');
                  } else if (['hr_manager', 'hr_specialist', 'hr'].includes(user?.role)) {
                    navigate('/admin/hr/employees');
                  } else {
                    setActiveView('orders');
                  }
                }}
                data-testid="header-logo"
              />
              <div className="h-5 w-px bg-slate-200 mx-1.5 shrink-0" />
              {/* Dashboard — ADMIN-HOME-002 (Jun 2026): for admins the tab is
                  split into "Home" (Admin Dashboard portal at /admin) and
                  "Operations" (this production dashboard). Other roles keep
                  the single "Dashboard" tab unchanged. */}
              {user?.role === 'admin' ? (
                <>
                  <button
                    onClick={() => navigate('/admin')}
                    data-testid="nav-home-btn"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  >
                    <Home className="h-3.5 w-3.5" />
                    Home
                  </button>
                  <button
                    onClick={() => setActiveView('orders')}
                    data-testid="nav-operations-btn"
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                      activeView === 'orders'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <MonitorCog className="h-3.5 w-3.5" />
                    Operations
                  </button>
                </>
              ) : isJR ? (
                <>
                  {/* RBAC-PILOT-ORDERS-001: JR reaches the Order Desk shell only via a
                      matrix grant; shell switching lives in the WorkspaceSwitcher. */}
                  <button
                    onClick={() => navigate('/dashboard/orders')}
                    data-testid="nav-orders-btn"
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                      activeView === 'orders'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <ClipboardList className="h-3.5 w-3.5" />
                    Dashboard
                  </button>
                </>
              ) : (!isITRole(user?.role) && !isAccountingRole(user?.role) && !['hr_manager', 'hr_specialist', 'hr'].includes(user?.role)) && (
                <button 
                  onClick={() => setActiveView(user?.role === 'typing_processor' ? 'typing' : (isProcessorWorkspaceView ? 'processor-orders' : 'orders'))}
                  data-testid="nav-dashboard-btn"
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                    (activeView === 'orders' || (user?.role === 'typing_processor' && activeView === 'typing') || (isProcessorWorkspaceView && activeView === 'processor-orders'))
                      ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Dashboard
                </button>
              )}
              {/* USER-DUAL-ROLE-UI-001: additional processor role → explicit processor queue entry */}
              {(holdsRole(user, 'processor') || holdsRole(user, 'search_processor') || holdsRole(user, 'title_search_processor'))
                && !['processor', 'search_processor', 'title_search_processor', 'admin'].includes(user?.role) && (
                <button
                  onClick={() => navigate('/dashboard/processor-orders')}
                  data-testid="nav-processor-queue-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Processing Queue
                </button>
              )}
              {/* USER-DUAL-ROLE-UI-001: additional QC Specialist role → explicit QC Dashboard entry */}
              {holdsRole(user, 'qc_specialist') && user?.role !== 'qc_specialist' && user?.role !== 'admin' && (
                <button
                  onClick={() => navigate('/qc-dashboard')}
                  data-testid="nav-qc-dashboard-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  QC Dashboard
                </button>
              )}
              {/* TYPING-DUAL-ROLE-001: additional Typing role → explicit Typing Queue entry */}
              {holdsTypingRole(user) && user?.role !== 'typing_processor' && user?.role !== 'admin' && (
                <button
                  onClick={() => setActiveView('typing')}
                  data-testid="nav-typing-queue-btn"
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                    activeView === 'typing' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Typing Queue
                </button>
              )}
              
              {/* My Workspace — visible to all roles. May 17, 2026 (production rollout):
                  Previously hidden for it_manager / it_support / accounting. Per RBAC update,
                  these roles now get nav access to /dashboard/workspace (route-level guards
                  also allow the single path while keeping operational dashboards
                  blocked). HR / Jurisdiction roles have their own nav entry points. */}
              {!['hr_manager', 'hr_specialist', 'hr'].includes(user?.role) && (
                <button 
                  onClick={() => setActiveView('my-workspace')}
                  data-testid="nav-workspace-btn"
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                    activeView === 'my-workspace' 
                      ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <User className="h-3.5 w-3.5" />
                  My Workspace
                </button>
              )}

              {/* Team (TL/Admin + HR/IT Manager: Team Management only — TEAM-SCOPE-002) */}
              {['admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader', 'hr_manager', 'it_manager'].includes(user?.role) && (
                <DropdownMenu open={teamMenuOpen} onOpenChange={setTeamMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      data-testid="nav-team-btn"
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                        activeView === 'team-management' || window.location.pathname.includes('/team/')
                          ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <Users className="h-3.5 w-3.5" />
                      Team
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem 
                      onClick={() => { setTeamMenuOpen(false); setActiveView('team-management'); }}
                      className="cursor-pointer"
                      data-testid="team-management-nav-link"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Team Management
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { setTeamMenuOpen(false); navigate('/training/whiteboard?from=dashboard'); }}
                      className="cursor-pointer"
                      data-testid="training-whiteboard-nav-link"
                    >
                      <PenLine className="h-4 w-4 mr-2" />
                      Training Whiteboard
                    </DropdownMenuItem>
                    {!['hr_manager', 'it_manager'].includes(user?.role) && (<>
                    <DropdownMenuItem 
                      onClick={() => { setTeamMenuOpen(false); navigate('/team/auto-assignment'); }}
                      className="cursor-pointer"
                      data-testid="auto-assignment-nav-link"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Auto Assignment
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => { setTeamMenuOpen(false); navigate('/team/edit-requests'); }}
                      className="cursor-pointer"
                      data-testid="edit-requests-nav-link"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Requests
                    </DropdownMenuItem>
                    </>)}
                    {/* PR 1 (Phase B1) — Break Management entry for TL roles.
                        Admins reach the same URLs from the Admin sidebar; this
                        dropdown is the TL surface. Backend role gate at the
                        endpoints + BreakManagementProtectedRoute on the routes
                        keeps this safe even if the menu leaks. */}
                    {!['hr_manager', 'it_manager'].includes(user?.role) && (<>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => { setTeamMenuOpen(false); navigate('/hr/breaks/active'); }}
                      className="cursor-pointer"
                      data-testid="nav-break-management-link"
                    >
                      <Coffee className="h-4 w-4 mr-2" />
                      Break Management
                    </DropdownMenuItem>
                    </>)}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Analytics (TL/Admin/HR) */}
              {['admin', 'hr', 'hr_manager', 'hr_specialist', 'team_leader', 'tax_team_leader', 'title_team_leader', 'tax_search_team_leader', 'title_search_team_leader', 'manager', 'supervisor'].includes(user?.role) && (
                <button 
                  onClick={() => navigate('/analytics')}
                  data-testid="reports-nav-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all text-slate-600 hover:text-slate-900 hover:bg-slate-100 whitespace-nowrap"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Analytics
                </button>
              )}

              {/* Vendor Manager specific buttons */}
              {user?.role === 'vendor_manager' && (
                <>
                  <Button 
                    variant={activeView === 'favorites' ? 'default' : 'ghost'} 
                    size="sm"
                    onClick={() => setActiveView('favorites')}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Manage Favorites
                  </Button>
                </>
              )}
            </div>

            {/* CENTER ZONE: Status Indicators */}
            <div className="flex items-center gap-2">
              {/* Session Health Indicator - Shows user's activity status (hide for IT roles - they have it in IT Dashboard) */}
              {!['it_manager', 'it_support'].includes(user?.role) && (
                <SessionHealthIndicator />
              )}
              
              {/* Break Button - Start/end breaks (hide for IT roles - they have it in IT Dashboard) */}
              {!['it_manager', 'it_support'].includes(user?.role) && (
                <BreakButton />
              )}
              
              {/* System Downtime Button - Report technical issues blocking work (hide for IT roles - not relevant) */}
              {!['it_manager', 'it_support'].includes(user?.role) && (
                <SystemDowntimeButton />
              )}
            </div>

            {/* RIGHT ZONE: Notifications & Profile */}
            <div className="flex items-center gap-2">
              {/* WORKSPACE-SWITCHER-001 — renders only for multi-workspace users */}
              <WorkspaceSwitcher user={user} tone={headerIsBlue ? 'dark' : 'light'} />

              {/* Team Activity Panel — trigger + drawer (Admin/TL only) */}
              <TeamActivityPanel currentUser={user} />

              {/* P0 QC Workflow - Notification Bell */}
              <NotificationBell user={user} />

              {/* User Profile Dropdown - Modern Badge Design.
                  LOGOUT-FLICKER-002: modal={false} — the default modal menu
                  scroll-locks <body> (removes the scrollbar), shifting page
                  width on open and again on unmount during logout. */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <div className="user-badge" data-testid="profile-chip">
                    {/* Circular Initials Badge */}
                    <div className="initials">
                      {user?.full_name?.split(' ').map(name => name[0]).join('').toUpperCase().substring(0, 2) || 'OU'}
                    </div>
                    
                    {/* User Name & Role - Reduced Size */}
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-xs font-semibold">
                        {user?.full_name || 'Order Desk User'}
                      </span>
                      <span className="text-[10px] opacity-75">
                        {getRoleDisplayName(user?.role)}
                      </span>
                    </div>
                    
                    {/* Dropdown Chevron */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </DropdownMenuTrigger>
                
                {/* Enhanced Dropdown Menu */}
                <DropdownMenuContent align="end" className="w-52 mt-2 border-0 shadow-lg">
                  {/* Accounting users see Product Master / Client Pricing / Billing
                      here as a safety net because their role isn't allow-listed
                      for AdminLayout. */}
                  {user?.role === 'accounting' && (
                    <>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/products')}
                        className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-purple-50"
                        data-testid="products-menu-item"
                      >
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Package className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Product Master</p>
                          <p className="text-xs text-gray-500">Manage products & workflows</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/pricing')}
                        className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-green-50"
                        data-testid="pricing-menu-item"
                      >
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Client Pricing</p>
                          <p className="text-xs text-gray-500">Manage client price books</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/billing')}
                        className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-blue-50"
                        data-testid="billing-menu-item"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Billing Dashboard</p>
                          <p className="text-xs text-gray-500">Invoices & payments</p>
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Logout - Available for all users */}
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-red-50 text-red-600"
                    data-testid="profile-menu-logout"
                  >
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <LogOut className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-600">Logout</p>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
  );
};


// UNIFIED-SHELL-001: inside the premium module shell the classic blue header is replaced by the shell's utility bar.
export const AppHeader = (props) => (useInShell() ? null : <AppHeaderInner {...props} />);
export default AppHeader;
